/**
 * Bonapisca - Main Application
 * Phase 3.1 Frontend implementing Three Pillars architecture
 */

import React, { lazy, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation, useParams } from 'react-router-dom';
import { useAuthStore } from './stores/authStore';
import { MobileLayout } from './components/layout/MobileLayout';
import { LoadingSpinner } from './components/ui/LoadingSpinner';
import ErrorBoundary from './components/ui/ErrorBoundary';
import { PerformanceMonitor } from './hooks/usePerformanceMonitoring';

import HomePlannerPreview from './features/planner/HomePlannerPreview';
import ProtectedRoute from './components/auth/ProtectedRoute';
import AnalyticsRouteTracker from './components/analytics/AnalyticsRouteTracker';
import Reveal from './components/ui/Reveal';
import Seo from './components/seo/Seo';
import { changeLanguage } from './services/i18n';
import type { SupportedLocale } from './types';
import { PUBLIC_ACCOUNT_FEATURES_ENABLED } from './config/publicFeatures';
import CookieBanner from './components/legal/CookieBanner';
import ScrollToTop from './components/layout/ScrollToTop';

const CodexPage = lazy(() => import('./features/codex/CodexPage'));
const CodexHomepage = lazy(() => import('./features/codex/CodexPage').then((module) => ({ default: module.CodexHomepage })));
const ToolboxPage = lazy(() => import('./features/toolbox/ToolboxPage'));
const LogbookPage = PUBLIC_ACCOUNT_FEATURES_ENABLED ? lazy(() => import('./features/logbook/LogbookPage')) : null;
const BeginnerSetupAdvisorPage = lazy(() => import('./features/onboarding/BeginnerSetupAdvisorPage'));
const SeasonalPlannerPage = lazy(() => import('./features/planner/SeasonalPlannerPage'));
const RouteSetupPage = lazy(() => import('./features/planner/RouteSetupPage'));
const AffiliateDisclosurePage = lazy(() => import('./components/legal/AffiliateDisclosurePage'));
const LegalDocumentPage = lazy(() => import('./components/legal/LegalDocumentPage'));

const LOCALE_PREFIXES: Record<string, SupportedLocale> = {
  en: 'en-GB',
  it: 'it-IT',
  es: 'es-ES',
  fr: 'fr-FR',
  de: 'de-DE',
};

const LocalePathSync: React.FC = () => {
  const location = useLocation();

  useEffect(() => {
    const prefix = location.pathname.split('/')[1];
    const locale = LOCALE_PREFIXES[prefix];
    if (!locale) {
      return;
    }

    void changeLanguage(locale);
  }, [location.pathname]);

  return null;
};

const RequireLocale: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { locale } = useParams();

  if (!locale || !LOCALE_PREFIXES[locale]) {
    return <Navigate to="/" replace />;
  }

  return <>{children}</>;
};

const localized = (element: React.ReactNode) => <RequireLocale>{element}</RequireLocale>;

const HomePage: React.FC = () => {
  const { t } = useTranslation('common');

  return (
    <div className="brand-page">
      <Seo
        title={t('seo.home.title')}
        description={t('seo.home.description')}
        path="/"
      />
      <div className="brand-shell max-w-5xl pt-4 md:pt-6">
        <Reveal>
          <HomePlannerPreview />
        </Reveal>
      </div>
    </div>
  );
};

function App() {
  const { t } = useTranslation('common');
  const { isLoading, checkAuthStatus } = useAuthStore();

  useEffect(() => {
    const initializeApp = async () => {
      if (import.meta.env.DEV) {
        console.log('🚀 Initializing app...');
      }

      try {
        if (PUBLIC_ACCOUNT_FEATURES_ENABLED) {
          await checkAuthStatus();
        }
      } catch (error) {
        if (import.meta.env.DEV) {
          console.error('❌ App init failed:', error);
        }
      }
    };

    initializeApp();
  }, []);

  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }

    if (sessionStorage.getItem('post-login-reload') !== '1') {
      return;
    }

    sessionStorage.removeItem('post-login-reload');

    const timer = window.setTimeout(() => {
      window.location.reload();
    }, 300);

    return () => window.clearTimeout(timer);
  }, []);

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-stone-50">
        <div className="text-center space-y-4">
          <div className="text-6xl">🌊</div>
          <LoadingSpinner size="lg" />
          <p className="text-stone-600">{t('ui.loading.app')}</p>
        </div>
      </div>
    );
  }

  return (
    <ErrorBoundary>
      <Router>
        <MobileLayout>
          <AnalyticsRouteTracker />
          <LocalePathSync />
          <ScrollToTop />
          <PerformanceMonitor
            enabled={import.meta.env.DEV && import.meta.env.VITE_SHOW_PERF === 'true'}
            position="top-right"
          />

          <Routes>
            <Route path="/" element={<HomePage />} />
            <Route path="/:locale" element={localized(<HomePage />)} />
            <Route path="/affiliate-disclosure" element={<AffiliateDisclosurePage />} />
            <Route path="/:locale/affiliate-disclosure" element={localized(<AffiliateDisclosurePage />)} />
            <Route path="/terms" element={<LegalDocumentPage kind="terms" />} />
            <Route path="/:locale/terms" element={localized(<LegalDocumentPage kind="terms" />)} />
            <Route path="/privacy" element={<LegalDocumentPage kind="privacy" />} />
            <Route path="/:locale/privacy" element={localized(<LegalDocumentPage kind="privacy" />)} />
            <Route path="/cookies" element={<LegalDocumentPage kind="cookies" />} />
            <Route path="/:locale/cookies" element={localized(<LegalDocumentPage kind="cookies" />)} />
            <Route path="/legal-info" element={<LegalDocumentPage kind="legalInfo" />} />
            <Route path="/:locale/legal-info" element={localized(<LegalDocumentPage kind="legalInfo" />)} />
            <Route path="/onboarding" element={<BeginnerSetupAdvisorPage />} />
            <Route path="/:locale/onboarding" element={localized(<BeginnerSetupAdvisorPage />)} />
            <Route path="/whats-on-now" element={<SeasonalPlannerPage />} />
            <Route path="/:locale/whats-on-now" element={localized(<SeasonalPlannerPage />)} />
            <Route path="/now/:routeId" element={<RouteSetupPage />} />
            <Route path="/:locale/now/:routeId" element={localized(<RouteSetupPage />)} />

            <Route path="/codex" element={<CodexHomepage />} />
            <Route path="/codex/" element={<CodexHomepage />} />
            <Route path="/codex/*" element={<CodexPage />} />
            <Route path="/:locale/codex" element={localized(<CodexHomepage />)} />
            <Route path="/:locale/codex/" element={localized(<CodexHomepage />)} />
            <Route path="/:locale/codex/*" element={localized(<CodexPage />)} />
            <Route path="/toolbox" element={<ToolboxPage />} />
            <Route path="/toolbox/*" element={<ToolboxPage />} />
            <Route path="/:locale/toolbox" element={localized(<ToolboxPage />)} />
            <Route path="/:locale/toolbox/*" element={localized(<ToolboxPage />)} />

            {PUBLIC_ACCOUNT_FEATURES_ENABLED && LogbookPage ? (
              <>
                <Route
                  path="/logbook"
                  element={
                    <ProtectedRoute>
                      <LogbookPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/logbook/*"
                  element={
                    <ProtectedRoute>
                      <LogbookPage />
                    </ProtectedRoute>
                  }
                />
              </>
            ) : null}

            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
          <CookieBanner />
        </MobileLayout>
      </Router>
    </ErrorBoundary>
  );
}

export default App;
